/**
 * THE OWNER'S SCREEN.
 *
 * Anthony: "a dashboard option that shows a reporting function... like
 * rev/leads/conversion etc in tiles and a dashboard like our old Sales
 * Desk was. This can live under my profile menu/admin area."
 *
 * ADMIN AND SUPERVISOR ONLY, and the guard is worth being clear about: it
 * is in the interface, and the interface is not a security boundary. Row
 * level security stops a technician reading another SHOP's takings, which
 * is the part that must not be argued with. Hiding this page from a
 * technician in their own shop is about not putting the owner's margin on
 * a screen in a car park — a workflow decision, honestly enforced where
 * workflow decisions live.
 */

import { report, byTechnician, ppiByStaff, type Metric } from "../data/reports.ts";
import { viaLabel } from "../domain/ppi.ts";
import { SPANS, type Span } from "../data/funnel.ts";
import { esc, mount } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

const MAY_SEE: Role[] = ["admin", "supervisor"];

let span: Span = "month";

function card(m: Metric): string {
  return `
    <div class="mtile ${m.kind}${m.known ? "" : " unknown"}">
      <div class="mtile-l">${esc(m.label)}</div>
      <div class="mtile-v">${esc(m.value)}</div>
      <div class="mtile-n">${esc(m.note)}</div>
    </div>`;
}

export async function reportsPage(role: Role): Promise<void> {
  if (!MAY_SEE.includes(role)) {
    mount(`<div class="page"><header class="page-head"><h1>Reports</h1></header>
      <div class="card pad"><p class="quiet">These are the shop's takings, so they are kept to
      the owner and supervisors. If you need a figure from here, ask the desk.</p></div></div>`);
    return;
  }

  mount(`<div class="page">
    <header class="page-head">
      <a class="back" href="#/">&larr; Desk</a>
      <h1>Reports</h1>
      <p>What the shop did, and what it turned into money.</p>
    </header>
    <div class="tabs wide" id="rspan">
      ${SPANS.map((s) => `<button class="tab ${s.key === span ? "on" : ""}" data-s="${s.key}">${
        esc(s.label)}</button>`).join("")}
    </div>
    <div class="mtiles" id="rtiles"><div class="loading">Counting&hellip;</div></div>
    <div id="rtech"></div>
    <div id="rppi"></div>
    <p class="quiet" style="margin-top:14px;line-height:1.6">
      Money is counted from invoices and payments, never from a job&rsquo;s status &mdash; a job
      marked paid with no invoice behind it is a problem to see, not a figure to add.
      Most of the older history came across from the previous system, which recorded cash
      taken at the curb inconsistently, so older periods understate what was really collected.
    </p>
  </div>`);

  document.querySelectorAll<HTMLButtonElement>("#rspan .tab").forEach((b) =>
    b.addEventListener("click", () => { span = b.dataset["s"] as Span; void reportsPage(role); }));

  const tiles = document.getElementById("rtiles");
  const tech = document.getElementById("rtech");

  try {
    const [metrics, people] = await Promise.all([report(span), byTechnician(span)]);
    if (tiles) tiles.innerHTML = metrics.map(card).join("");

    if (tech) {
      // A bar each, scaled to the busiest person. A table of counts makes
      // you do the comparing; a bar has already done it.
      const top = people[0]?.jobs ?? 0;
      tech.innerHTML = people.length ? `
        <div class="card">
          <div class="card-head">Who finished the work</div>
          <div class="pad">
            ${people.map((p) => `
              <div class="bar-row">
                <span class="bar-name">${esc(p.name)}</span>
                <span class="bar-track"><span class="bar-fill" style="width:${
                  top ? Math.round((p.jobs / top) * 100) : 0}%"></span></span>
                <span class="bar-n">${p.jobs}</span>
              </div>`).join("")}
          </div>
        </div>` : "";
    }
    /* Inspections, by who took them and from which desk. A failure here
       says so in its own card rather than taking the page down. */
    const ppiCard = document.getElementById("rppi");
    if (ppiCard) {
      try {
        const ppi = await ppiByStaff(span);
        ppiCard.innerHTML = ppi.length ? `
          <div class="card">
            <div class="card-head">Pre-purchase inspections, by who took them</div>
            <div class="pad">
              ${ppi.map((p) => `
                <div class="bar-row">
                  <span class="bar-name">${esc(p.name)} <span class="quiet">via ${esc(viaLabel(p.via))}</span></span>
                  <span class="bar-track"><span class="bar-fill" style="width:${
                    Math.round((p.jobs / (ppi[0]?.jobs || 1)) * 100)}%"></span></span>
                  <span class="bar-n">${p.jobs}</span>
                </div>`).join("")}
            </div>
          </div>` : "";
      } catch (e) {
        ppiCard.innerHTML = `<div class="empty bad">The inspection count would not load &mdash; ${
          esc(e instanceof Error ? e.message : String(e))}</div>`;
      }
    }
  } catch (e) {
    if (tiles) {
      tiles.innerHTML = `<div class="empty bad">Those would not add up &mdash; ${
        esc(e instanceof Error ? e.message : String(e))}</div>`;
    }
  }
}
